<script lang="ts" setup>
import Layout from "@/Shared/Layout.vue";
import { Link, router, usePage } from "@inertiajs/vue3";
import WarningIcon from "@icons/warning-icon.svg";
import AlertIcon from "@icons/error-red.svg";
import Cms from "@/components/Cms.vue";
import { ref, computed, inject, onMounted, onBeforeUnmount } from "vue";
import CustomSelect from "@/components/CustomSelect.vue";
import { useGenericStore } from "@/store/genericStore";
import { storeToRefs } from "pinia";
import APIKey from "@/components/APIKey.vue";
let location = ref(null)
let builderData = ref([]);
let searchParams = computed(()=>{
    return {
        "data.pageType.$eq": "Api",
        "data.versions.$or": [
            {
                "$elemMatch": {
                    "versionNumber": selectedVersion.value.key
                }
            },
            {
                "$exists": false
            }
        ]
    }
})

let route = inject('route');
let url = computed(()=>{

    let urlString = location.value?.replace(`${window.location.origin}/index`, "")
    urlString = urlString.replace("/preview", '')
    // if(urlString.includes("/api/developer/")) {
    //     return urlString.replace("/api/developer/", "")
    // } else {
        return urlString
    // }
})
let slugify = (text: string) => {
    return text.toString().toLowerCase()
        .replace(/\s+/g, '-')           // Replace spaces with -
        .replace(/[^\w\-]+/g, '')       // Remove all non-word chars
        .replace(/\-\-+/g, '-')         // Replace multiple - with single -
        .replace(/^-+/, '')             // Trim - from start of text
        .replace(/-+$/, '');            // Trim - from end of text
}
let links = computed(()=>{
    let urlLinks = builderData.value.map((item: any)=>{
    let urlLink = item.pageName == "About The Api" ? route('api.docs') : route('api.docs', {pageName: slugify(item.pageName), version: selectedVersion.value.key})
    let urlEnding = urlLink.replace(`${window.location.origin}/index`, "")
    urlEnding = urlEnding.replace("/preview", '')
    let isCurrent = url.value == urlEnding
    return {
        label: item.pageName,
        url: urlLink,
        current: isCurrent,
        section: item.section,
        deprecated: item.versions?.find((version: any)=>version.versionNumber == selectedVersion.value.key)?.deprecated ?? false,
        removedAt: item.versions?.find((version: any)=>version.versionNumber == selectedVersion.value.key)?.removedAt ?? null,
    }
})
    let map = new Map();
    return urlLinks.reduce((acc: any, link: any) => {
        if (!acc.has(link.section)) {
            acc.set(link.section, []);
        }
        let links = acc.get(link.section)
        links.push(link)
        acc.set(link.section, links);
        return acc;
    }, map);
})

let { state: availableVersions } = storeToRefs(useGenericStore('availableVersions', []));

let versions = computed(()=>{
    return availableVersions.value.map((version: string)=>{
        return {
            id: version,
            key: version,
            value: version
        }
    })
})
router.on('success', (event)=>{
    location.value = event.detail.page.url.replace(/(index)\/(preview\/)?/, '')
})

function isWarning(deprecated: boolean, removedAt: string | null) {
    return deprecated && removedAt == null || new Date(removedAt) > new Date();
}
function isAlert(deprecated: boolean, removedAt: string | null) {
    return deprecated && removedAt != null && new Date(removedAt) < new Date();
}

let version = ref(route().params.version ?? 'v2');

let selectedVersion = computed({
    get(){
        return {
            key: version.value,
            value: version.value,
            id: version.value
        }
    },
    set(newVersion){
        router.visit(route('api.docs', {pageName: route().params.pageName, version: newVersion.key}))
    }
})


function updateVersion(newVersion: string){
    isTokensPage.value = route().current() == 'account.apiTokens';
    isApiPortalPage.value = route().current() == 'account.apiPortal';
    version.value = route().params.version ?? 'v2';
}



let isSetupPage = computed(()=>{
    return usePage().props?.content?.data?.pageUrl === 'setup-and-installation';
})

let apiType = computed(()=>{
    return usePage().props?.content?.data?.versions?.find((version: any)=>version.versionNumber == selectedVersion.value.key)?.type ?? 'graphql';
})

let hasTokens = computed(()=>{
    return usePage().props.apiTokenCount > 0;
})
let apiKey = computed(()=>{
    return usePage().props.api_key?.key ?? '';
})

let isTokensPage = ref(route().current() == 'account.apiTokens');

let isApiPortalPage = ref(route().current() == 'account.apiPortal');

let removeListener = () => {};

let canViewApiKey = computed(()=>{
    return !(isTokensPage.value || isApiPortalPage.value);
})

onMounted(()=>{
    location.value = window.location.href;
    removeListener = router.on('navigate', updateVersion)
})

onBeforeUnmount(()=>{
    removeListener?.();
})
</script>
<template>
    <Layout>
        <cms v-show="false" v-model="builderData" driver="builder" model="content-section" :readable-fields="['pageName', 'section','versions']" is-data :search-params="searchParams" />
        <div class="api-settings">
            <div class="api-settings-nav">
                <div class="flex-row align-center justify-between">
                    <Link :href="route('account.show')" class="api-settings-nav-backCta" aria-label="Back to Account">Back to Account</Link>
                    <a v-if="apiType == 'graphql'" class="api-settings-nav-cta" href="/rothco-api-playground" target="_blank" aria-label="Visit Playground">Visit Playground</a>
                </div>
                <div class="api-settings-nav-list">
                    <span class="headline-s">Rothco API</span>
                    <span class="headline-xs">Getting Started</span>
                    <Link :class="{
                        'api-nav-link': true,
                        current: link.current,
                        'flex-row align-center': true
                    }" v-for="(link, linkIndex) of links.get('Getting Started')" :href="link.url" :key="linkIndex"><AlertIcon class="error" v-if="isAlert(link.deprecated, link.removedAt )"  />
                    <WarningIcon v-if="isWarning(link.deprecated, link.removedAt)" />
                    <span>{{ link.label }}</span></Link>
                    <span class="headline-xs">Endpoints</span>
                    <Link :class="{
                        'api-nav-link': true,
                        current: link.current,
                        'flex-row align-center': true
                    }" v-for="(link, linkIndex) of links.get('Endpoints')" :href="link.url" :key="linkIndex">
                    <AlertIcon class="error" v-if="isAlert(link.deprecated, link.removedAt)"  />
                    <WarningIcon v-else-if="isWarning(link.deprecated, link.removedAt)" />
                    <span>{{ link.label }}</span></Link>
                    <span class="headline-xs">Tools</span>
                    <Link :class="{
                        'api-nav-link': true,
                        current: link.current,
                        'flex-row align-center': true
                    }" v-for="(link, linkIndex) of links.get('Tools')" :href="link.url" :key="linkIndex">
                    <AlertIcon class="error" v-if="isAlert(link.deprecated, link.removedAt)"  />
                    <WarningIcon v-else-if="isWarning(link.deprecated, link.removedAt)" />
                    <span>{{ link.label }}</span></Link>
                </div>
            </div>
            <div class="api-settings-nav-mobile">
                <div class="nav-selector bg-rothcoCream">
                    <details class="nav-selector__details">
                        <summary class="nav-selector__title">
                            API Portal Menu
                        </summary>
                        <div class="nav-selector__content">
                            <ul class="navlinks">
                                <li><span class="headline-xs">Getting Started</span></li>
                                <li><Link :class="{
                                    current: link.current
                                }" v-for="(link, linkIndex) of links.get('Getting Started')" :href="link.url" :key="linkIndex">{{ link.label }}</Link></li>
                                <li><span class="headline-xs">Endpoints</span></li>
                                <li><Link :class="{
                                    current: link.current
                                }" v-for="(link, linkIndex) of links.get('Endpoints')" :href="link.url" :key="linkIndex">{{ link.label }}</Link></li>
                                <li><span class="headline-xs">Tools</span></li>
                                <li><Link :class="{
                                    current: link.current
                                }" v-for="(link, linkIndex) of links.get('Tools')" :href="link.url" :key="linkIndex">{{ link.label }}</Link></li>
                            </ul>
                        </div>
                    </details>
                </div>
                <div class="flex justify-between full-width">
                <Link :href="route('account.show')" class="api-settings-nav-backCta" aria-label="Back to Account">Back to Account</Link>
                <a v-if="apiType == 'graphql'" class="api-settings-nav-cta" href="/rothco-api-playground" target="_blank" aria-label="Visit Playground">Visit Playground</a>
                </div>
            </div>

            <div class="api-settings-content-container" style="flex-direction: column;">
                <div class="api-settings-versions">
                    <APIKey v-if="canViewApiKey " :api_key="apiKey"  :allow_generate_key="isSetupPage" :has-tokens="hasTokens" :graph-ql="apiType == 'graphql'"/>
                    <custom-select v-show="canViewApiKey && versions.length > 1" :options="versions" v-model="selectedVersion" label="Version" />
                </div>
                <slot></slot>
            </div>
        </div>  
    </Layout>

</template>
<style lang="scss" scoped>
    .api-nav-link {
        display: flex;
        align-items: center;
        gap: 0.5rem;

        svg {
            width: 1.5rem;
            height: 1.5rem;
        }
    }
</style>